/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import * as React from 'react'
import { InputBase as BaseInput } from '@mui/material'
import { Box } from '@mui/system'
import { ChangeEvent, Dispatch, Fragment, SetStateAction, useRef } from 'react'
import { OtpInputStyles } from './OtpInput.styles'
import {
  primaryColors,
  transparentColors
} from '@/assets/styles/helpers/colors.styles'

function OTP({
  length,
  value,
  onChange
}: {
  length: number
  value: string
  onChange: Dispatch<SetStateAction<string>>
}) {
  const inputRefs = useRef<HTMLInputElement[]>(new Array(length).fill(null))

  const inputIds = useRef<string[]>(
    new Array(length)
      .fill(null)
      .map((_, i) => `otp-input-${Math.random()}-${i}`)
  )

  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex]
    targetInput.focus()
  }

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex]
    targetInput.select()
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault()
        break
      case 'ArrowLeft':
        event.preventDefault()
        if (currentIndex > 0) {
          focusInput(currentIndex - 1)
          selectInput(currentIndex - 1)
        }
        break
      case 'ArrowRight':
        event.preventDefault()
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1)
          selectInput(currentIndex + 1)
        }
        break
      case 'Delete':
        event.preventDefault()
        onChange(prevOtp => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1)
          return otp
        })

        break
      case 'Backspace':
        event.preventDefault()
        if (currentIndex > 0) {
          focusInput(currentIndex - 1)
          selectInput(currentIndex - 1)
        }

        onChange(prevOtp => {
          const otp =
            prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1)
          return otp
        })
        break

      default:
        break
    }
  }

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    const currentValue = event.target.value
    let indexToEnter = 0

    while (indexToEnter <= currentIndex) {
      if (
        inputRefs.current[indexToEnter].value &&
        indexToEnter < currentIndex
      ) {
        indexToEnter += 1
      } else {
        break
      }
    }
    onChange(prev => {
      const otpArray = prev.split('')
      const lastValue = currentValue[currentValue.length - 1]
      otpArray[indexToEnter] = lastValue
      return otpArray.join('')
    })
    if (currentValue !== '') {
      if (currentIndex < length - 1) {
        focusInput(currentIndex + 1)
      }
    }
  }

  const handleClick = (
    _: React.MouseEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    selectInput(currentIndex)
  }

  const handlePaste = (event: ClipboardEvent, currentIndex: number) => {
    event.preventDefault()
    const { clipboardData } = event

    if (clipboardData?.types.includes('text/plain')) {
      let pastedText = clipboardData.getData('text/plain')
      pastedText = pastedText.substring(0, length).trim()
      let indexToEnter = 0

      while (indexToEnter <= currentIndex) {
        if (
          inputRefs.current[indexToEnter].value &&
          indexToEnter < currentIndex
        ) {
          indexToEnter += 1
        } else {
          break
        }
      }

      const otpArray = value.split('')

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? ' '
        otpArray[i] = lastValue
      }

      onChange(otpArray.join(''))
    }
  }

  const activeIndex = Math.min(value.length, length - 1)
  const isComplete = value.length >= length

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1.5rem',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {new Array(length).fill(null).map((_, index) => {
        const isActive = !isComplete && index === activeIndex
        const isFilled = Boolean(value[index])

        return (
          <Fragment key={inputIds.current[index]}>
            <BaseInput
              type="password"
              sx={{
                ...OtpInputStyles,
                ...(isActive && {
                  borderColor: primaryColors.white
                }),
                ...(isFilled && {
                  backgroundColor: transparentColors.whiteShade2
                })
              }}
              aria-label={`Digit ${index + 1} of OTP`}
              inputRef={(ele: HTMLInputElement | null) => {
                if (ele) inputRefs.current[index] = ele
              }}
              value={value[index] ?? ''}
              onKeyDown={event => handleKeyDown(event as any, index)}
              onChange={event => handleChange(event as any, index)}
              onClick={event => handleClick(event as any, index)}
              onPaste={event => handlePaste(event as any, index)}
              readOnly
            />
          </Fragment>
        )
      })}
    </Box>
  )
}

interface OTPInputProps {
  setOtp: Dispatch<SetStateAction<string>>
  otp: string
}

export default function OTPInput(props: OTPInputProps) {
  const { otp, setOtp } = props

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <OTP value={otp} onChange={setOtp} length={6} />
    </Box>
  )
}
